import React, { useState, useEffect, useCallback } from 'react';
import {
  Zap, Plus, Edit2, Copy, Trash2, MessageSquare, CheckSquare,
  Clock, ChevronRight, Activity, Tag, Server, Layers,
} from 'lucide-react';
import { useAutomation } from '../context/AutomationContext';
import { useSettings } from '../context/SettingsContext';
import { useCompany } from '../context/CompanyContext';
import { auth } from '../firebase';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import PlaybookModal from '../playbooks/PlaybookModal';

/**
 * Playbooks Page
 *
 * Shows two sections:
 *   1. Backend Playbooks (server-side execution, stored in companies/{cId}/playbooks)
 *   2. Legacy Automations (client-side execution, stored in companies/{cId}/automations)
 *
 * Plus an Execution Logs panel.
 */

const BACKEND_TRIGGER_EVENTS = [
  'quote_created', 'quote_sent', 'quote_approved',
  'job_scheduled', 'job_completed',
  'invoice_sent', 'invoice_paid',
];

const TRIGGER_LABELS = {
  quote_created: 'Quote created',
  quote_sent: 'Quote sent',
  quote_approved: 'Quote approved',
  job_scheduled: 'Job scheduled',
  job_completed: 'Job completed',
  invoice_sent: 'Invoice sent',
  invoice_paid: 'Invoice paid',
  leadCreated: 'Lead created',
  stageChanged: 'Stage changed',
};

const STATUS_STYLES = {
  success: 'bg-green-100 text-green-700',
  partial_error: 'bg-yellow-100 text-yellow-700',
  error: 'bg-red-100 text-red-700',
  skipped_cooldown: 'bg-gray-100 text-gray-600',
  skipped_conditions: 'bg-gray-100 text-gray-600',
  skipped_once_per_entity: 'bg-gray-100 text-gray-600',
  no_actions: 'bg-gray-100 text-gray-600',
};

const getTriggerLabel = (pb) => {
  if (!pb) return '—';
  const ev = pb.triggerEvent || pb.trigger?.type;
  if (!ev) return '—';
  if (ev === 'stageChanged') {
    const from = pb.trigger?.from || 'Any';
    const to = pb.trigger?.to || 'Any';
    return `Stage: ${from} → ${to}`;
  }
  return TRIGGER_LABELS[ev] || ev;
};

const getActionLabel = (pb) => {
  // Backend playbook: has actions array
  if (pb.actions?.length) {
    const a = pb.actions[0];
    if (a.type === 'send_sms') return `SMS: "${(a.config?.message || '').slice(0, 30)}..."`;
    if (a.type === 'create_task') return `Task: ${a.config?.title || 'Untitled'}`;
    if (a.type === 'add_tag') return `Tag: ${a.config?.tag || '?'}`;
    return a.type;
  }
  // Legacy automation: has action object
  const action = pb.action;
  if (!action) return '—';
  if (action.type === 'createTask') return `Task: ${action.task?.title || 'Untitled'}`;
  if (action.type === 'sendSMS') return `SMS: "${(action.sms?.message || '').slice(0, 30)}..."`;
  return action.type;
};

const getActionIcon = (pb) => {
  const type = pb.actions?.[0]?.type || pb.action?.type || '';
  if (['send_sms', 'sendSMS'].includes(type)) return MessageSquare;
  if (type === 'add_tag') return Tag;
  return CheckSquare;
};

const getDelayLabel = (delay) => {
  if (!delay || !delay.value) return 'Immediately';
  const unit = delay.unit === 'hours' ? 'hr' : 'day';
  return `After ${delay.value} ${unit}${delay.value > 1 ? 's' : ''}`;
};

const isActive = (pb) => pb.isActive !== undefined ? pb.isActive : pb.active !== false;

// ─── Execution Logs Panel ─────────────────────────────────────────────────────

const ExecutionLogs = ({ companyId }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLogs = useCallback(async () => {
    try {
      const token = await auth.currentUser?.getIdToken(false);
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/playbooks/logs?companyId=${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json().catch(() => ({}));
      setLogs(data.logs || []);
    } catch (err) {
      console.error('[Playbooks] Fetch logs error:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) fetchLogs();
  }, [companyId, fetchLogs]);

  if (loading) {
    return <div className="text-sm text-gray-500 py-4 text-center">Loading logs...</div>;
  }

  if (!logs.length) {
    return (
      <div className="text-sm text-gray-500 py-6 text-center">
        No execution logs yet. Logs appear here when playbooks fire.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {logs.map((log) => (
        <div
          key={log.id}
          className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border border-gray-100 text-sm"
          data-testid={`log-entry-${log.id}`}
        >
          <div className="flex-1 min-w-0">
            <span className="font-medium text-gray-800 truncate">
              {log.playbookName || log.playbookId}
            </span>
            <span className="mx-2 text-gray-400">·</span>
            <span className="text-gray-500">
              {TRIGGER_LABELS[log.triggerEvent] || log.triggerEvent}
            </span>
          </div>
          <span
            className={`px-2 py-0.5 text-xs font-medium rounded-full flex-shrink-0 ${STATUS_STYLES[log.status] || 'bg-gray-100 text-gray-600'}`}
          >
            {log.status?.replace(/_/g, ' ')}
          </span>
          <span className="text-xs text-gray-400 flex-shrink-0">
            {log.executedAt
              ? new Date(log.executedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
              : '—'}
          </span>
        </div>
      ))}
    </div>
  );
};

// ─── Playbook Card ─────────────────────────────────────────────────────────────

const PlaybookCard = ({ pb, onEdit, onDuplicate, onDelete, onToggle, isBackend }) => {
  const ActionIcon = getActionIcon(pb);
  const active = isActive(pb);

  return (
    <div
      className={`bg-white rounded-xl border border-gray-200 p-5 transition-all duration-200 ${
        active ? 'hover:border-orange-300 hover:shadow-md' : 'opacity-60'
      }`}
      data-testid={`playbook-card-${pb.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <h3 className="text-base font-semibold text-gray-900 truncate">{pb.name}</h3>
            {isBackend && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-emerald-50 text-emerald-700 rounded border border-emerald-200">
                <Server className="w-3 h-3" />
                Server
              </span>
            )}
            {pb.isDefault && (
              <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-orange-50 text-orange-600 rounded border border-orange-200">
                Default
              </span>
            )}
            {pb.delayHours > 0 && (
              <span className="inline-flex items-center px-1.5 py-0.5 text-xs font-medium bg-blue-50 text-blue-600 rounded border border-blue-200">
                +{pb.delayHours}h delay
              </span>
            )}
            {!active && (
              <span className="px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-500 rounded">Disabled</span>
            )}
          </div>

          <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
            <span className="font-medium text-gray-700">{getTriggerLabel(pb)}</span>
            {!isBackend && (
              <>
                <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                <span className="flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5 text-gray-400" />
                  {getDelayLabel(pb.delay)}
                </span>
              </>
            )}
            <ChevronRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
            <span className="flex items-center gap-1">
              <ActionIcon className="w-3.5 h-3.5 text-gray-400" />
              {getActionLabel(pb)}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Switch
            checked={active}
            onCheckedChange={(checked) => onToggle(pb, checked)}
            data-testid={`playbook-toggle-${pb.id}`}
          />
          <div className="flex items-center gap-1 border-l border-gray-200 pl-3">
            <button
              onClick={() => onEdit(pb)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Edit"
              data-testid={`playbook-edit-${pb.id}`}
            >
              <Edit2 className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={() => onDuplicate(pb)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Duplicate"
            >
              <Copy className="w-4 h-4 text-gray-500" />
            </button>
            <button
              onClick={() => onDelete(pb)}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
              title="Delete"
              data-testid={`playbook-delete-${pb.id}`}
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────

const Playbooks = () => {
  const {
    playbooks,            // backend playbooks (companies/{cId}/playbooks)
    automations,          // legacy automations (companies/{cId}/automations)
    patchPlaybook,
    updatePlaybook,
    deletePlaybook,
    duplicatePlaybook,
    updateAutomation,
    deleteAutomation,
    duplicateAutomation,
  } = useAutomation();
  const { pipelineStages } = useSettings();
  const { companyId: companyIdFromCtx } = useCompany();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingPlaybook, setEditingPlaybook] = useState(null);
  const [activeTab, setActiveTab] = useState('playbooks'); // 'playbooks' | 'logs'

  // Use companyId from context (reliable) with fallback to playbook/automation data
  const companyId = companyIdFromCtx || playbooks[0]?.companyId || automations[0]?.companyId || '';

  const handleCreate = () => {
    setEditingPlaybook(null);
    setModalOpen(true);
  };

  const handleEdit = (pb) => {
    setEditingPlaybook(pb);
    setModalOpen(true);
  };

  const handleDuplicate = async (pb) => {
    try {
      const isBackend = BACKEND_TRIGGER_EVENTS.includes(pb.triggerEvent);
      if (isBackend) {
        await duplicatePlaybook(pb.id);
      } else {
        await duplicateAutomation(pb.id);
      }
      toast.success('Playbook duplicated');
    } catch (err) {
      toast.error('Failed to duplicate');
    }
  };

  const handleDelete = async (pb) => {
    if (!window.confirm(`Delete "${pb.name}"? This cannot be undone.`)) return;
    try {
      const isBackend = BACKEND_TRIGGER_EVENTS.includes(pb.triggerEvent);
      if (isBackend) {
        await deletePlaybook(pb.id);
      } else {
        await deleteAutomation(pb.id);
      }
      toast.success('Playbook deleted');
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleToggle = async (pb, enabled) => {
    try {
      const isBackend = BACKEND_TRIGGER_EVENTS.includes(pb.triggerEvent);
      if (isBackend) {
        await patchPlaybook(pb.id, { isActive: enabled });
      } else {
        await updateAutomation(pb.id, { active: enabled });
      }
      toast.success(enabled ? 'Enabled' : 'Disabled');
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingPlaybook(null);
  };

  const totalCount = playbooks.length + automations.length;

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
            <Zap className="w-5 h-5 text-orange-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Playbooks</h1>
            <p className="text-sm text-gray-500">Server-side automation — runs reliably, no browser needed</p>
          </div>
        </div>
        <Button
          onClick={handleCreate}
          className="bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="create-playbook-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Playbook
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
        <button
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'playbooks' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('playbooks')}
          data-testid="tab-playbooks"
        >
          <Layers className="w-3.5 h-3.5" />
          Playbooks
          {totalCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-gray-200 text-gray-600 rounded-full">
              {totalCount}
            </span>
          )}
        </button>
        <button
          className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'logs' ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('logs')}
          data-testid="tab-logs"
        >
          <Activity className="w-3.5 h-3.5" />
          Execution Logs
        </button>
      </div>

      {/* ── Playbooks Tab ── */}
      {activeTab === 'playbooks' && (
        <div className="space-y-6">
          {/* Empty State */}
          {totalCount === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">
                <Zap className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No playbooks yet</h3>
              <p className="text-sm text-gray-500 mb-6 max-w-md mx-auto">
                Create a playbook to automatically follow up on quotes, send invoice reminders,
                or trigger tasks — all without a browser session.
              </p>
              <Button
                onClick={handleCreate}
                className="bg-orange-500 hover:bg-orange-600 text-white"
              >
                <Plus className="w-4 h-4 mr-2" />
                Create Your First Playbook
              </Button>
            </div>
          )}

          {/* Backend Playbooks */}
          {playbooks.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Server className="w-4 h-4 text-emerald-600" />
                <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Server-side Playbooks
                </h2>
                <span className="text-xs text-gray-400">({playbooks.length})</span>
              </div>
              {playbooks.map((pb) => (
                <PlaybookCard
                  key={pb.id}
                  pb={pb}
                  isBackend
                  onEdit={handleEdit}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                />
              ))}
            </div>
          )}

          {/* Automations */}
          {automations.length > 0 && (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-gray-400" />
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  Automations
                </h2>
                <span className="text-xs text-gray-400">({automations.length})</span>
              </div>
              {automations.map((pb) => (
                <PlaybookCard
                  key={pb.id}
                  pb={pb}
                  isBackend={false}
                  onEdit={handleEdit}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                  onToggle={handleToggle}
                />
              ))}
            </div>
          )}

          {/* Info Panel */}
          <div className="bg-gray-50 rounded-xl border border-gray-100 p-5">
            <div className="flex items-start gap-3">
              <Server className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-600 space-y-1">
                <p className="font-medium text-gray-700">How Server-side Playbooks Work</p>
                <p>
                  When a trigger event fires (e.g., quote sent), KRO automatically evaluates all
                  matching active playbooks and executes their actions — even if no one is logged in.
                  A 24-hour cooldown prevents duplicate sends per entity.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Execution Logs Tab ── */}
      {activeTab === 'logs' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-2 mb-4">
            <Activity className="w-4 h-4 text-gray-500" />
            <h2 className="text-sm font-semibold text-gray-700">Recent Executions</h2>
          </div>
          <ExecutionLogs companyId={companyId} />
        </div>
      )}

      {/* Create/Edit Modal */}
      <PlaybookModal
        open={modalOpen}
        onClose={handleModalClose}
        playbook={editingPlaybook}
        pipelineStages={pipelineStages}
      />
    </div>
  );
};

export default Playbooks;
